import { InvoiceTab } from '@numeo/types-core'
import { useCallback } from 'react'
import invoiceApi from 'services/invoiceApi'
import { ExistingInvoiceBatchOption } from '../types/invoiceBatch'
import { rowsToExistingInvoiceBatchOptions } from '../utils/existingInvoiceBatchOptions'
import { useDebouncedAsyncSearch } from './useDebouncedAsyncSearch'

const emptyExistingBatchOptions = (): ExistingInvoiceBatchOption[] => []

class ExistingBatchSearchResponseError extends Error {}

const getExistingBatchErrorMessage = (error: unknown): string =>
    error instanceof ExistingBatchSearchResponseError && error.message ? error.message : 'Failed to load existing batches'

export const useExistingInvoiceBatchSearch = ({
    enabled,
    search,
}: {
    enabled: boolean
    search: string
}): { options: ExistingInvoiceBatchOption[]; loading: boolean; error: string | null } => {
    const fetcher = useCallback(async ({ search, signal }: { search: string; signal: AbortSignal }) => {
        const response = await invoiceApi.listInvoiceRows(
            {
                tab: InvoiceTab.INVOICED,
                page: 1,
                limit: 100,
                search: search || undefined,
                batchOnly: true,
                appendableOnly: true,
            },
            signal
        )
        if (!response.success || !response.data) throw new ExistingBatchSearchResponseError(response.message || 'Failed to load existing batches')
        return rowsToExistingInvoiceBatchOptions(response.data.rows)
    }, [])
    const { data, loading, error } = useDebouncedAsyncSearch({
        enabled,
        search,
        emptyData: emptyExistingBatchOptions,
        fetcher,
        logMessage: '[Invoices] Failed to search existing invoice batches',
        getErrorMessage: getExistingBatchErrorMessage,
    })

    return { options: data, loading, error }
}
